import { Link } from 'react-router-dom';
import {
  Bell,
  Briefcase,
  ChevronRight,
  Database,
  Layers,
  LogOut,
  Package,
  Receipt,
  Ruler,
  Tag,
  TrendingUp,
  User,
  Users,
  Wallet,
} from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { hapticNotify } from '@/lib/telegram';

interface SectionItem {
  to: string;
  label: string;
  description?: string;
  icon: any;
  ownerOnly?: boolean;
  hideForViewer?: boolean;
  tint: 'primary' | 'accent' | 'success' | 'warning' | 'muted';
}

const OPERATIONS: SectionItem[] = [
  { to: '/stock', label: 'Склад волос', description: 'Партии и движения', icon: Package, hideForViewer: true, tint: 'primary' },
  { to: '/expenses', label: 'Расходы', description: 'Чеки и категории', icon: Receipt, hideForViewer: true, tint: 'accent' },
  { to: '/other-incomes', label: 'Прочие доходы', description: 'Косметика и продажи', icon: TrendingUp, hideForViewer: true, tint: 'success' },
];

const CATALOGS: SectionItem[] = [
  { to: '/settings/services', label: 'Услуги', description: 'Цены и длительности', icon: Briefcase, ownerOnly: true, tint: 'primary' },
  { to: '/settings/extras', label: 'Доп. услуги', description: 'Добавки к процедурам', icon: Layers, ownerOnly: true, tint: 'primary' },
  { to: '/settings/lengths', label: 'Длины волос', description: 'Справочник длин', icon: Ruler, ownerOnly: true, tint: 'primary' },
  { to: '/settings/tags', label: 'Теги клиентов', description: 'Цветные категории', icon: Tag, ownerOnly: true, tint: 'accent' },
  { to: '/settings/expense-categories', label: 'Категории расходов', description: 'Группировка трат', icon: Wallet, ownerOnly: true, tint: 'accent' },
];

const ACCOUNT: SectionItem[] = [
  { to: '/settings/notifications', label: 'Уведомления', description: 'Расписание и алерты', icon: Bell, tint: 'warning' },
  { to: '/settings/masters', label: 'Работники', description: 'Команда', icon: Users, ownerOnly: true, tint: 'success' },
  { to: '/settings/profile', label: 'Профиль', description: 'Имя и рабочие часы', icon: User, tint: 'muted' },
];

const tintBg: Record<SectionItem['tint'], string> = {
  primary: 'bg-primary-soft text-primary',
  accent: 'bg-accent-soft text-accent',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  muted: 'bg-muted text-muted-foreground',
};

export const SettingsPage = () => {
  const { master, clear } = useAuthStore();
  const isOwner = master?.role === 'owner';
  const isViewer = master?.role === 'viewer';
  const visible = (items: SectionItem[]) =>
    items.filter((s) => (isOwner || !s.ownerOnly) && (!isViewer || !s.hideForViewer));

  const backupNow = async () => {
    try {
      await api.post('/backup/now');
      hapticNotify('success');
      alert('Бэкап создан');
    } catch (e: any) {
      hapticNotify('error');
      alert(e?.response?.data?.error ?? 'Ошибка');
    }
  };

  const sections = [
    { title: 'Операции', items: visible(OPERATIONS) },
    { title: 'Справочники', items: visible(CATALOGS) },
    { title: 'Аккаунт', items: visible(ACCOUNT) },
  ];

  return (
    <div className="animate-in-up space-y-5 pb-2">
      <h1 className="text-2xl font-bold tracking-tight">Меню</h1>

      {/* Profile card */}
      <Card className="bg-aurora-card overflow-hidden">
        <CardContent className="flex items-center gap-3 p-4">
          <Avatar src={master?.photo_url} name={master?.name} size="xl" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-lg font-bold">{master?.name}</div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <Badge variant={isOwner ? 'primary' : 'default'}>
                {isOwner ? '★ Владелец' : isViewer ? 'Только календарь' : 'Работник'}
              </Badge>
              {master?.telegram_id && (
                <span className="text-xs text-muted-foreground tabular">
                  TG {master.telegram_id}
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {sections.map((s) =>
        s.items.length ? (
          <div key={s.title} className="space-y-2">
            <div className="px-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              {s.title}
            </div>
            <Card className="overflow-hidden">
              {s.items.map((item, i) => (
                <Link key={item.to} to={item.to}>
                  <div
                    className={`flex items-center gap-3 p-3 transition-colors hover:bg-secondary ${
                      i > 0 ? 'border-t border-border/50' : ''
                    }`}
                  >
                    <span
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                        tintBg[item.tint]
                      }`}
                    >
                      <item.icon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold">{item.label}</div>
                      {item.description && (
                        <div className="text-xs text-muted-foreground">
                          {item.description}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </Card>
          </div>
        ) : null,
      )}

      {isOwner && (
        <Button variant="outline" onClick={backupNow} className="w-full">
          <Database className="h-4 w-4" /> Сделать бэкап сейчас
        </Button>
      )}

      <Button variant="ghost" onClick={clear} className="w-full text-destructive hover:bg-destructive-soft">
        <LogOut className="h-4 w-4" /> Выйти
      </Button>

      <div className="pt-2 text-center text-[11px] tabular text-muted-foreground">
        Chernova CRM · v1.0
      </div>
    </div>
  );
};
